import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { formatCurrency, formatDate } from '../utils/format';
import { getErrorMessage } from '../utils/error';
import '../styles/SignInPage.css';

interface FactureDuLien {
  id: string;
  fournisseur: string | null;
  date: string | null;
  montant: number | null;
  debut: string | null;
  fin: string | null;
  consommation: number | null;
  unite: string | null;
  validee: boolean;
}

// Client SANS session : un autre compte ouvert dans ce navigateur (bailleur
// expiré, locataire) ferait refuser la validation à la place du lien.
const sansSession = axios.create({ baseURL: API_URL });

/**
 * Cible du lien de l'email récapitulatif : valider ses factures sans se
 * connecter. Route PUBLIQUE ; le lien porte l'autorisation. Ouvrir la page ne
 * valide rien — les messageries ouvrent les liens d'elles-mêmes —, seul le
 * bouton le fait.
 */
const ValiderFacturesPage: React.FC = () => {
  const [params] = useSearchParams();
  const jeton = params.get('jeton') ?? '';
  const [factures, setFactures] = useState<FactureDuLien[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [validees, setValidees] = useState<number | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!jeton) {
      setErreur("Ce lien n'est pas valable.");
      return;
    }
    // Le jeton dans le corps, jamais dans l'adresse : le serveur journalise les adresses.
    sansSession
      .post('/validation-factures/lecture', { jeton })
      .then((r) => setFactures(r.data.factures))
      .catch((err) => setErreur(getErrorMessage(err, "Ce lien n'est pas valable.")));
  }, [jeton]);

  const valider = async () => {
    setEnvoi(true);
    try {
      const r = await sansSession.post('/validation-factures', { jeton });
      setValidees(r.data.validees);
    } catch (err) {
      setErreur(getErrorMessage(err, 'La validation a échoué. Réessayez.'));
    } finally {
      setEnvoi(false);
    }
  };

  const restantes = factures?.filter((f) => !f.validee).length ?? 0;

  return (
    <div className="signin-container">
      <div className="signin-card">
        <h1 className="signin-title">Coloc App</h1>

        {erreur ? (
          <>
            <h2 className="signin-subtitle">Lien inutilisable</h2>
            <p>{erreur}</p>
            <div className="signin-links">
              <Link to="/signin" className="forgot-password-link">Se connecter</Link>
            </div>
          </>
        ) : validees !== null ? (
          <>
            <h2 className="signin-subtitle">C&apos;est fait</h2>
            <p>{validees} facture(s) validée(s).</p>
          </>
        ) : factures === null ? (
          <h2 className="signin-subtitle">Chargement…</h2>
        ) : (
          <>
            <h2 className="signin-subtitle">Valider vos factures</h2>
            <ul className="valider-factures-liste">
              {factures.map((f) => (
                <li key={f.id}>
                  <strong>{f.fournisseur ?? 'Facture'}</strong> — {formatCurrency(f.montant)}
                  {f.date && ` — ${formatDate(f.date)}`}
                  {f.debut && f.fin && ` — du ${formatDate(f.debut)} au ${formatDate(f.fin)}`}
                  {f.consommation != null && ` — ${f.consommation} ${f.unite === 'm3' ? 'm³' : f.unite ?? ''}`}
                  {f.validee && ' (déjà validée)'}
                </li>
              ))}
            </ul>
            {restantes > 0 ? (
              <button type="button" className="signin-button" onClick={valider} disabled={envoi}>
                {envoi ? 'Validation…' : `Valider ${restantes} facture(s)`}
              </button>
            ) : (
              <p>Il n&apos;y a plus rien à valider dans ce lien.</p>
            )}
            <p className="valider-factures-note">
              Un montant vous semble faux ? Ne validez pas : connectez-vous et
              corrigez-le dans l&apos;écran Factures.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ValiderFacturesPage;
